/**
 * ч.806 (рішення власника 2026-09-12): місця діяльності (відокремлені
 * підрозділи) юрособи — таблиця `.tch` на вкладці «Податкові рахунки».
 * Рядок = друге місце обліку з 20-ОПП: назва (тип обʼєкта — іде у рядок 033
 * Податкового розрахунку за підрозділ), КАТОТТГ (рядок 032), отримувач ПДФО
 * (рахунок місцевого бюджету місця) + шаблон призначення. Працівник
 * привʼязується до місця у картці; ПДФО по ньому йде окремою платіжкою, а сам
 * він показується у ДОДАТКОВОМУ розрахунку (лише 4ДФ), не в основному.
 * ФОП не стосується — картка ховає таблицю.
 */
import type { OrgActivityPlace, OrganizationCreate } from '@/api/catalogs';
import { findBankByIban, isValidUaIban, type BankRow } from '@/shared/ukr_classifiers';
import { DEFAULT_TAX_PURPOSE_TEMPLATE, TAX_PURPOSE_VARS_HINT } from '@/shared/taxPurposeTemplate';
import KatottgPicker from '@/shared/components/KatottgPicker';
import { signalUserEdit } from '@/store/formDrafts';
import TchTable from '@/shared/components/TchTable';

const cleanIban = (v: string) => v.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 34);

const EMPTY_ROW: OrgActivityPlace = {
  id: null, name: '', object_type_code: null, katottg: null, address: null,
  pdfo_recipient_name: null, pdfo_recipient_edrpou: null, pdfo_recipient_iban: null,
  pdfo_purpose_template: null,
};

export default function OrgActivityPlacesTable(props: {
  form: OrganizationCreate;
  setForm: (f: OrganizationCreate) => void;
  banks: BankRow[];
  style?: React.CSSProperties;
}) {
  const { form, setForm, banks, style } = props;
  const rows: OrgActivityPlace[] = form.activity_places ?? [];
  // ч.1059: додати/прибрати рядок — правка користувача (чернетка картки).
  const setRows = (r: OrgActivityPlace[]) => {
    signalUserEdit();
    setForm({ ...form, activity_places: r });
  };
  const patch = (i: number, p: Partial<OrgActivityPlace>) =>
    setRows(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));

  return (
    <div style={style}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: 4, flexWrap: 'wrap' }}>
        <b style={{ fontSize: '0.85rem' }}>Місця діяльності (відокремлені підрозділи)</b>
        <span style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
          ПДФО працівників місця — до місцевого бюджету за КАТОТТГ місця; по них подається
          окремий Податковий розрахунок (лише 4ДФ). Працівник привʼязується до місця у картці.
        </span>
      </div>
      <TchTable>
        <thead>
          <tr>
            <th style={{ width: 200 }}>Назва (тип обʼєкта з 20-ОПП)</th>
            <th style={{ width: 70 }}>Код 20-ОПП</th>
            <th style={{ width: 250 }}>КАТОТТГ</th>
            <th style={{ width: 200 }}>Отримувач ПДФО</th>
            <th style={{ width: 110 }}>Код отримувача</th>
            <th style={{ width: 250 }}>IBAN</th>
            <th title={TAX_PURPOSE_VARS_HINT}>Призначення платежу (шаблон)</th>
            <th style={{ width: 36 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const bank = findBankByIban(row.pdfo_recipient_iban ?? '', banks);
            return (
              <tr key={row.id ?? `n-${i}`}>
                <td>
                  <input value={row.name} placeholder="ЗЕМЕЛЬНА ДІЛЯНКА, МАГАЗИН…"
                    onChange={(e) => patch(i, { name: e.target.value.toUpperCase() })} />
                </td>
                <td>
                  <input value={row.object_type_code ?? ''} maxLength={10}
                    onChange={(e) => patch(i, { object_type_code: e.target.value || null })} />
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input value={row.katottg ?? ''} maxLength={20} placeholder="UA…"
                      style={{ flex: 1 }}
                      onChange={(e) => patch(i, { katottg: e.target.value.trim().toUpperCase() || null })} />
                    <KatottgPicker onPick={(code) => patch(i, { katottg: code })} />
                  </div>
                </td>
                <td>
                  <input value={row.pdfo_recipient_name ?? ''}
                    onChange={(e) => patch(i, { pdfo_recipient_name: e.target.value || null })} />
                </td>
                <td>
                  <input value={row.pdfo_recipient_edrpou ?? ''} maxLength={10}
                    onChange={(e) => patch(i, { pdfo_recipient_edrpou: e.target.value.replace(/\D/g, '') || null })} />
                </td>
                <td>
                  <input value={row.pdfo_recipient_iban ?? ''} maxLength={34} title={bank ?? undefined}
                    style={row.pdfo_recipient_iban && !isValidUaIban(row.pdfo_recipient_iban)
                      ? { borderColor: '#d33' } : undefined}
                    onChange={(e) => patch(i, { pdfo_recipient_iban: cleanIban(e.target.value) || null })} />
                </td>
                <td>
                  <input value={row.pdfo_purpose_template ?? ''} maxLength={420}
                    placeholder={DEFAULT_TAX_PURPOSE_TEMPLATE} title={TAX_PURPOSE_VARS_HINT}
                    onChange={(e) => patch(i, { pdfo_purpose_template: e.target.value || null })} />
                </td>
                <td style={{ textAlign: 'center' }}>
                  <button type="button" title="видалити місце" style={{ padding: '0 6px' }}
                    onClick={() => setRows(rows.filter((_, j) => j !== i))}>×</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </TchTable>
      <button type="button" style={{ marginTop: 4 }} onClick={() => setRows([...rows, { ...EMPTY_ROW }])}>
        + додати місце діяльності
      </button>
    </div>
  );
}
